import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Trophy, ArrowSquareOut, Warning, Info, ShieldCheck } from "@phosphor-icons/react";
import api, {
  CATEGORIES,
  PUBLIC_READ_TIMEOUT_MS,
  categoryLabel,
  formatGBPDecimal,
} from "../lib/api";
import { publicPrizeCell } from "../lib/displayValue";
import AffiliateLinkLabel from "../components/AffiliateLinkLabel";
import SEO from "../components/SEO";
import PrizeImage, { SourceOverlay, sourceActionForCompetition } from "../components/PrizeImage";
import BudgetAmountControl from "../components/BudgetAmountControl";
import OperatorMultiSelect from "../components/OperatorMultiSelect";
import { sanitisePublicBudget } from "../lib/publicCompetitionContract";

const SORT_TABS = [
  { value: "best_balanced", label: "Best balanced", hint: "Odds + value + transparency + trust" },
  { value: "best_chance", label: "Best chance", hint: "Strongest estimated odds for your budget" },
  { value: "best_value", label: "Best value", hint: "Highest value score" },
  { value: "highest_prize", label: "Highest prize", hint: "Biggest prize values" },
];
const PAGE_SIZE = 24;

const queryIdentity = ({ budget, category, sort, operatorKeys }) => JSON.stringify({
  budget: Number(budget),
  category,
  sort,
  operatorKeys: operatorKeys || "",
});

const validateBudgetResponse = (payload, expected, { requireVersion = false } = {}) => {
  if (
    !payload
    || typeof payload !== "object"
    || !Array.isArray(payload.results)
    || !Number.isInteger(payload.total_matching)
    || Number(payload.budget) !== Number(expected.budget)
    || String(payload.category || "all") !== expected.category
    || String(payload.sort || "best_balanced") !== expected.sort
    || (requireVersion && !payload.result_version)
  ) {
    throw new Error("malformed_budget_odds_response");
  }
  return payload;
};

const requestErrorMessage = (error, { loadingMore = false } = {}) => {
  if (error?.response?.status === 409) {
    return "Competition data changed while these results were open. Refresh to continue with one current result set.";
  }
  if (error?.code === "ECONNABORTED") {
    return "Budget Odds took too long to respond. Try again.";
  }
  if (error?.message === "malformed_budget_odds_response") {
    return "Budget Odds returned an incomplete response. Try again.";
  }
  return loadingMore
    ? "More Budget Odds results could not be loaded. Try again."
    : "Budget Odds could not be calculated right now. Try again.";
};

const parseOperatorKeysParam = (searchParams) => {
  const raw = [
    searchParams.get("operator_keys"),
    ...searchParams.getAll("operator_key"),
  ].filter(Boolean).join(",");
  const keys = raw
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter(Boolean);
  return keys.length ? Array.from(new Set(keys)) : null;
};

const isBudgetRowRenderable = (row) => row?.budget_odds_eligible !== false;

export default function BestOddsForBudget() {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialBudget = sanitisePublicBudget(searchParams.get("budget"), 20);
  const initialCategory = searchParams.get("category") || "all";
  const initialSort = searchParams.get("sort") || "best_balanced";
  const initialOperatorKeys = parseOperatorKeysParam(searchParams);

  const [budget, setBudget] = useState(initialBudget);
  const [category, setCategory] = useState(initialCategory);
  const [sort, setSort] = useState(initialSort);
  const [selectedOperatorKeys, setSelectedOperatorKeys] = useState(initialOperatorKeys);
  const [retryToken, setRetryToken] = useState(0);
  const [state, setState] = useState({
    data: null,
    dataQueryKey: null,
    loading: true,
    loadingMore: false,
    error: null,
  });
  const requestSequence = useRef(0);
  const initialController = useRef(null);
  const loadMoreController = useRef(null);
  const activeQueryKey = useRef(null);
  const { loading, loadingMore, error } = state;
  const hasNoOperators = Array.isArray(selectedOperatorKeys) && selectedOperatorKeys.length === 0;
  const operatorKeysParam = selectedOperatorKeys?.length
    ? selectedOperatorKeys.join(",")
    : undefined;
  const queryKey = useMemo(() => queryIdentity({
    budget,
    category,
    sort,
    operatorKeys: operatorKeysParam,
  }), [budget, category, sort, operatorKeysParam]);
  activeQueryKey.current = queryKey;
  const data = state.dataQueryKey === queryKey ? state.data : null;
  const operatorOptions = data?.operators || state.data?.operators || [];

  useEffect(() => () => {
    initialController.current?.abort();
    loadMoreController.current?.abort();
  }, []);

  useEffect(() => {
    initialController.current?.abort();
    loadMoreController.current?.abort();
    const controller = new AbortController();
    const requestId = ++requestSequence.current;
    initialController.current = controller;
    const next = new URLSearchParams();
    next.set("budget", String(budget));
    if (category !== "all") next.set("category", category);
    if (sort !== "best_balanced") next.set("sort", sort);
    if (operatorKeysParam) next.set("operator_keys", operatorKeysParam);
    setSearchParams(next, { replace: true });

    if (hasNoOperators) {
      setState((previous) => ({
        data: previous.data ? {
          ...previous.data,
          count: 0,
          total_matching: 0,
          results: [],
          has_more: false,
          next_offset: null,
        } : null,
        dataQueryKey: queryKey,
        loading: false,
        loadingMore: false,
        error: null,
      }));
      return () => controller.abort();
    }

    setState((previous) => ({
      data: previous.data,
      dataQueryKey: previous.dataQueryKey,
      loading: true,
      loadingMore: false,
      error: null,
    }));
    api.get("/budget-odds", {
      params: {
        budget,
        category,
        sort,
        limit: PAGE_SIZE,
        offset: 0,
        ...(operatorKeysParam ? { operator_keys: operatorKeysParam } : {}),
      },
      signal: controller.signal,
      timeout: PUBLIC_READ_TIMEOUT_MS,
    }).then((r) => {
      const nextData = validateBudgetResponse(r.data, {
        budget,
        category,
        sort,
      }, { requireVersion: Boolean(r.data?.has_more) });
      if (!controller.signal.aborted && requestId === requestSequence.current) {
        setState({
          data: nextData,
          dataQueryKey: queryKey,
          loading: false,
          loadingMore: false,
          error: null,
        });
      }
    }).catch((requestError) => {
      if (controller.signal.aborted || requestError?.code === "ERR_CANCELED") return;
      if (requestId !== requestSequence.current) return;
      setState((previous) => ({
        ...previous,
        loading: false,
        loadingMore: false,
        error: requestErrorMessage(requestError),
      }));
    });
    return () => controller.abort();
  }, [
    budget,
    category,
    sort,
    operatorKeysParam,
    hasNoOperators,
    queryKey,
    retryToken,
    setSearchParams,
  ]);

  const heading = useMemo(() => {
    const cat = category === "all" ? "" : `${categoryLabel(category).toLowerCase()} competition `;
    return `Best ${cat}odds for £${budget}`;
  }, [budget, category]);

  const visibleResults = useMemo(() => {
    const rows = (data?.results || []).filter(isBudgetRowRenderable);
    if (category === "all") return rows;
    return rows.filter((row) => {
      const finalCategory = String(row.category || row.category_slug || "").toLowerCase();
      return finalCategory === category;
    });
  }, [data, category]);

  const loadMore = async () => {
    if (!data?.has_more || data.next_offset == null || loadingMore) return;
    loadMoreController.current?.abort();
    const controller = new AbortController();
    const requestId = ++requestSequence.current;
    const requestedQueryKey = queryKey;
    const requestedVersion = data.result_version;
    loadMoreController.current = controller;
    setState((prev) => ({ ...prev, loadingMore: true, error: null }));
    try {
      const { data: nextPage } = await api.get("/budget-odds", {
        params: {
          budget,
          category,
          sort,
          limit: data.limit || PAGE_SIZE,
          offset: data.next_offset,
          result_version: requestedVersion,
          ...(operatorKeysParam ? { operator_keys: operatorKeysParam } : {}),
        },
        signal: controller.signal,
        timeout: PUBLIC_READ_TIMEOUT_MS,
      });
      validateBudgetResponse(nextPage, { budget, category, sort }, { requireVersion: true });
      if (
        controller.signal.aborted
        || requestId !== requestSequence.current
        || activeQueryKey.current !== requestedQueryKey
      ) return;
      if (nextPage.result_version !== requestedVersion) {
        const versionError = new Error("budget_odds_result_version_changed");
        versionError.response = { status: 409 };
        throw versionError;
      }
      setState((prev) => {
        if (prev.dataQueryKey !== requestedQueryKey) return prev;
        const existing = prev.data?.results || [];
        const seen = new Set(existing.map((row) => row.id || row.slug));
        const appended = (nextPage.results || []).filter((row) => {
          const identity = row.id || row.slug;
          if (!identity || seen.has(identity)) return false;
          seen.add(identity);
          return true;
        });
        return {
          data: {
            ...nextPage,
            results: [...existing, ...appended],
          },
          dataQueryKey: requestedQueryKey,
          loading: false,
          loadingMore: false,
          error: null,
        };
      });
    } catch (requestError) {
      if (controller.signal.aborted || requestError?.code === "ERR_CANCELED") return;
      if (requestId !== requestSequence.current) return;
      setState((prev) => ({
        ...prev,
        loadingMore: false,
        error: requestErrorMessage(requestError, { loadingMore: true }),
      }));
    } finally {
      if (loadMoreController.current === controller) {
        loadMoreController.current = null;
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
      <SEO
        title={`Best odds for your budget${category === "all" ? "" : " — " + categoryLabel(category)}`}
        description={`See what £${budget} really buys across UK ${category === "all" ? "" : categoryLabel(category).toLowerCase() + " "}prize competitions. Estimated odds, ticket counts and CompCheck Scores — numbers first.`}
        path="/best-odds-for-budget"
        queryString={searchParams.toString()}
        ogType="website"
      />
      <div className="max-w-3xl">
        <div className="overline text-[var(--cc-gold)] mb-2">Budget calculator</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
          Best odds for your budget
        </h1>
        <p className="text-[var(--cc-text-muted)] mt-3 leading-relaxed">
          Enter what you want to spend and compare what that budget really buys
          across active UK prize competitions. Numbers-first, no winning promises.
        </p>
      </div>

      {/* Budget panel */}
      <div className="mt-8 rounded-lg border border-white/10 bg-white/[0.03] p-6">
        <div className="grid md:grid-cols-[1fr_1fr] gap-6 items-start">
          <div>
            <BudgetAmountControl
              budget={budget}
              onBudgetChange={setBudget}
              showApply
            />
          </div>
          <div>
            <label className="overline text-white/50 block mb-2">Category</label>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="category-all"
                onClick={() => setCategory("all")}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  category === "all"
                    ? "bg-[var(--cc-gold)] border-[var(--cc-gold)] text-[var(--cc-navy)]"
                    : "border-white/20 text-white/70 hover:border-white/40"
                }`}
              >
                All
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  data-testid={`category-${c}`}
                  onClick={() => setCategory(c)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    category === c
                      ? "bg-[var(--cc-gold)] border-[var(--cc-gold)] text-[var(--cc-navy)]"
                      : "border-white/20 text-white/70 hover:border-white/40"
                  }`}
                >
                  {categoryLabel(c)}
                </button>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            {operatorOptions.length ? (
              <OperatorMultiSelect
                operators={operatorOptions}
                selectedKeys={selectedOperatorKeys}
                onChange={setSelectedOperatorKeys}
              />
            ) : (
              <div>
                <span className="overline text-white/50 block mb-2">Operators</span>
                <span className="text-xs text-white/45">Operators appear after the first calculation.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sort tabs */}
      <div className="mt-8 flex flex-wrap gap-1 border-b border-white/10">
        {SORT_TABS.map((t) => (
          <button
            key={t.value}
            data-testid={`sort-${t.value}`}
            onClick={() => setSort(t.value)}
            title={t.hint}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              sort === t.value
                ? "border-[var(--cc-gold)] text-white"
                : "border-transparent text-white/60 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Heading */}
      <div className="mt-8 flex items-end justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold" data-testid="results-heading">
          {heading}
        </h2>
        <div className="text-xs text-[var(--cc-text-muted)] font-mono-num">
          {data?.total_matching != null
            ? `${data.total_matching} competitions within your £${data.budget} budget with calculable odds.`
            : ""}
        </div>
      </div>

      {/* Results */}
      <div className="mt-6">
        {hasNoOperators && (
          <div
            role="status"
            data-testid="operator-selection-required"
            className="mb-4 rounded-md border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-sm text-amber-100"
          >
            Select at least one operator to calculate Budget Odds.
          </div>
        )}
        {data?.unsupported_operator_keys?.length > 0 && (
          <div
            data-testid="unsupported-operator-filter"
            className="mb-4 rounded-md border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-sm text-amber-100"
          >
            That operator filter is not supported. Choose an operator from the approved list.
          </div>
        )}
        {loading && (
          <div
            role="status"
            aria-live="polite"
            data-testid="budget-loading-status"
            className="mb-4 flex items-center justify-center gap-3 rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-[var(--cc-text-muted)]"
          >
            <span
              aria-hidden="true"
              className="h-4 w-4 animate-spin rounded-full border-2 border-white/25 border-t-[var(--cc-gold)]"
            />
            {data ? "Refreshing current Budget Odds..." : "Calculating Budget Odds..."}
          </div>
        )}
        {error && (
          <div
            role="alert"
            data-testid="budget-error"
            className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300/30 bg-amber-300/10 px-4 py-3 text-sm text-amber-100"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setRetryToken((value) => value + 1)}
              className="btn-outline px-3 py-1.5 rounded text-xs"
              data-testid="budget-retry"
            >
              Try again
            </button>
          </div>
        )}
        {loading && !data ? null : !data ? null : !visibleResults.length ? (
          <EmptyState category={category} />
        ) : data.count === 0 && category !== "all" ? (
          <EmptyState category={category} hasOnlyUncalculable />
        ) : data.count === 0 ? (
          <EmptyState category="all" hasOnlyUncalculable budget={budget} />
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              {visibleResults.map((r, i) => (
                <BudgetResultCard key={r.id} r={r} sort={sort} rank={i + 1} budget={budget} category={category} />
              ))}
            </div>
            {data.has_more && (
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={loadMore}
                  disabled={loadingMore}
                  data-testid="budget-load-more"
                  className="btn-outline px-5 py-2 rounded text-sm disabled:opacity-60"
                >
                  {loadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Disclaimer */}
      <div className="mt-12 rounded-lg border border-white/10 p-5 bg-white/[0.02]" data-testid="budget-disclaimer">
        <div className="overline text-[var(--cc-gold)] mb-2 inline-flex items-center gap-1.5">
          <Info size={12} weight="fill" /> About these numbers
        </div>
        <p className="text-xs text-[var(--cc-text-muted)] leading-relaxed">
          CompChecker does not sell tickets or operate competitions. Budget odds
          are estimates based on visible or manually entered total ticket caps and
          ticket prices. They do not improve your chance of winning. Always
          check the operator&apos;s official terms before entering.
        </p>
      </div>
    </div>
  );
}

const EmptyState = ({ category, hasOnlyUncalculable, budget }) => (
  <div className="border border-dashed border-white/15 rounded-lg p-12 text-center" data-testid="empty-state">
    <p className="text-white/80 font-display text-lg mb-2">
      {hasOnlyUncalculable
        ? budget != null
          ? `Your £${budget} budget is below every ticket price in this category.`
          : "These competitions don't have visible ticket caps."
        : "No active competitions found for this category yet."}
    </p>
    <p className="text-[var(--cc-text-muted)] text-sm">
      Try a different budget, or browse all categories.
    </p>
  </div>
);

export const BudgetResultCard = ({ r, sort, rank, budget, category }) => {
  const bv = r.budget_view;
  const s = r.scores;
  const cantCalc = bv.insufficient_budget || bv.max_tickets_missing || bv.invalid_ticket_price;
  const showAlternativesLink = bv.insufficient_budget;
  const [alternatives, setAlternatives] = useState(null);
  const [loadingAlt, setLoadingAlt] = useState(false);

  const fetchAlternatives = async () => {
    if (alternatives !== null) {
      setAlternatives(null); // toggle off
      return;
    }
    setLoadingAlt(true);
    try {
      const { data } = await api.get("/budget-odds/cheaper-alternatives", {
        params: { budget, category, limit: 4 },
      });
      setAlternatives(data.results || []);
    } finally {
      setLoadingAlt(false);
    }
  };

  const compScore = s.overall_compcheck_score;
  const compBand = s.score_band;
  // Iter 20.21.d / Message 763 — Top-right circle now shows the actual
  // CompCheck score so it can't be confused with a low "Balanced"
  // metric next to a healthy CompCheck. Red styling is reserved for
  // the Caution band (genuine risk) — Strong/Good/Mixed use neutral
  // gold borders.
  const compRing =
    compScore >= 65 ? "var(--cc-good)" :
    compScore >= 50 ? "var(--cc-warn)" :
    "var(--cc-bad)";

  // Iter 20.21.f / Message 765 — central helper; never falls back to
  // raw prize_value_estimate.
  const prize = publicPrizeCell(r);
  const displayLabel = prize.label;
  const valueCellValue = prize.value;
  const outboundAction = sourceActionForCompetition(r);
  const outboundCtaUrl = outboundAction?.href || "";
  const hasOutboundCta = Boolean(outboundAction);
  const budgetTicketCount =
    bv.effective_user_ticket_count ??
    bv.effective_tickets ??
    bv.budget_ticket_count ??
    bv.tickets_affordable;
  const totalCapSub = bv.total_ticket_cap
    ? `total cap ${Number(bv.total_ticket_cap).toLocaleString("en-GB")}`
    : null;

  return (
    <div className="min-w-0 rounded-lg bg-white border border-slate-200 p-5 flex flex-col gap-4" data-testid={`budget-card-${r.slug}`}>
      <div className="flex items-start gap-4">
        <div className="relative h-20 w-20 rounded-md overflow-hidden bg-slate-100 flex-none" data-testid={`budget-card-image-${r.slug}`}>
          <PrizeImage comp={r} variant="card" showOutboundOverlay={false} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-[var(--cc-text-dark-muted)] mb-1 flex flex-wrap items-center gap-2">
            #{rank} <span className="text-slate-400">·</span> {r.operator?.name} <span className="text-slate-400">·</span> {categoryLabel(r.category)}
          </div>
          <h3 className="font-display text-lg font-semibold text-[var(--cc-text-dark)] leading-tight line-clamp-2">
            {r.title}
          </h3>
        </div>
        <div
          className="h-12 w-12 rounded-full flex flex-col items-center justify-center text-[10px] font-bold font-mono-num border-2 text-[var(--cc-text-dark)] flex-none"
          style={{ borderColor: compRing }}
          data-testid={`budget-card-compcheck-${r.slug}`}
        >
          <span className="text-base leading-none">{compScore}</span>
          <span className="text-[8px] text-slate-500 leading-none mt-0.5">CompCheck</span>
        </div>
      </div>

      {hasOutboundCta && (
        <div className="flex flex-col items-start gap-1" data-testid={`budget-card-outbound-row-${r.slug}`}>
          <SourceOverlay overlay={outboundAction} comp={r} variant="card" inline testId={`budget-card-cta-${r.slug}`} />
          <AffiliateLinkLabel record={r} destinationUrl={outboundCtaUrl} testId={`budget-card-affiliate-label-${r.slug}`} />
        </div>
      )}

      {cantCalc ? (
        <div className="space-y-2">
          <div className="rounded bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex gap-2">
            <Warning size={14} weight="fill" className="flex-none mt-0.5" />
            <span>{bv.explanation}</span>
          </div>
          {budgetTicketCount > 0 && bv.max_tickets_missing && !bv.invalid_ticket_price && !bv.insufficient_budget && (
            <p className="text-xs text-[var(--cc-text-dark-muted)]">
              Your budget buys {budgetTicketCount} ticket{budgetTicketCount === 1 ? "" : "s"}, but odds are unavailable until the total ticket cap is clear.
            </p>
          )}
          {showAlternativesLink && (
            <>
              <button
                onClick={fetchAlternatives}
                data-testid={`show-alternatives-${r.slug}`}
                className="text-xs text-[var(--cc-bronze)] font-semibold hover:underline inline-flex items-center gap-1"
              >
                {alternatives !== null ? "Hide alternatives" : "Show me cheaper alternatives"}
                {!loadingAlt && <ArrowSquareOut size={11} weight="bold" />}
              </button>
              {loadingAlt && (
                <p className="text-xs text-[var(--cc-text-dark-muted)]">Finding affordable options...</p>
              )}
              {alternatives && (
                <div className="rounded bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid={`alternatives-${r.slug}`}>
                  <p className="text-[11px] text-[var(--cc-text-dark-muted)] leading-relaxed">
                    Here are competitions your budget can actually enter, based on ticket price and visible data.
                  </p>
                  {alternatives.length === 0 ? (
                    <p className="text-xs text-[var(--cc-text-dark)]">No affordable alternatives found yet.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {alternatives.map((alt) => (
                        <li key={alt.id} className="text-xs flex items-center justify-between gap-2">
                          <Link
                            to={`/competition/${alt.slug}?budget=${encodeURIComponent(budget)}`}
                            className="text-[var(--cc-text-dark)] hover:text-[var(--cc-bronze)] truncate font-medium"
                          >
                            {alt.title}
                            {!alt.same_category && (
                              <span className="ml-1 text-[10px] text-[var(--cc-text-dark-muted)]">({categoryLabel(alt.category)})</span>
                            )}
                          </Link>
                          <span className="font-mono-num text-[var(--cc-text-dark-muted)] flex-none">
                            {formatGBPDecimal(alt.ticket_price)} · {alt.budget_view.estimated_user_odds_text}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <Cell label="Your budget buys" value={`${bv.effective_tickets} ticket${bv.effective_tickets === 1 ? "" : "s"}`} sub={bv.capped_by_user_limit ? "capped by per-user limit" : null} />
          <Cell label="Estimated odds" value={bv.estimated_user_odds_text} sub={totalCapSub} highlight />
          <Cell label={displayLabel} value={valueCellValue} sub={formatGBPDecimal(r.ticket_price) + " / ticket"} testId={`budget-card-value-${r.slug}`} />
        </div>
      )}

      {!cantCalc && bv.explanation && (
        <p className="text-xs text-[var(--cc-text-dark-muted)] leading-relaxed">{bv.explanation}</p>
      )}

      <div className="flex flex-wrap gap-1.5">
        {compBand && (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
            style={{
              background:
                compBand === "Strong" || compBand === "Good" ? "rgba(16,185,129,0.10)" :
                compBand === "Mixed" ? "rgba(245,158,11,0.10)" :
                "rgba(220,38,38,0.10)",
              color:
                compBand === "Strong" || compBand === "Good" ? "#047857" :
                compBand === "Mixed" ? "#92400e" :
                "#991b1b",
              borderColor:
                compBand === "Strong" || compBand === "Good" ? "rgba(16,185,129,0.30)" :
                compBand === "Mixed" ? "rgba(245,158,11,0.30)" :
                "rgba(220,38,38,0.30)",
            }}
            data-testid={`budget-card-band-${r.slug}`}
          >
            {compBand}
          </span>
        )}
        {s.is_transparent && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
            <ShieldCheck size={10} weight="fill"/> Transparent
          </span>
        )}
        {!s.is_transparent && s.data_quality_label === "partial_data" && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            Partial data
          </span>
        )}
        {!s.is_transparent && s.data_quality_label === "check_terms" && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
            Check terms
          </span>
        )}
        {r.free_entry_available && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            Free entry route
          </span>
        )}
        {(s.risk_flags || []).length > 0 && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-800 border border-red-200">
            <Warning size={10} weight="fill"/> {s.risk_flags.length} risk flag{s.risk_flags.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-xs text-[var(--cc-text-dark-muted)]">
          <Trophy size={12} weight="fill" className="text-[var(--cc-bronze)]"/>
          CompCheck {s.overall_compcheck_score}
        </div>
          <Link
            to={`/competition/${r.slug}?budget=${encodeURIComponent(budget)}`}
            data-testid={hasOutboundCta ? `budget-card-breakdown-${r.slug}` : `budget-card-cta-${r.slug}`}
            className="text-xs text-[var(--cc-bronze)] font-semibold inline-flex items-center gap-1 hover:underline"
          >
            View breakdown <ArrowSquareOut size={12} />
          </Link>
      </div>
    </div>
  );
};

const Cell = ({ label, value, sub, highlight, testId }) => (
  <div className={`rounded p-2 ${highlight ? "bg-[var(--cc-navy)] text-white" : "bg-slate-50 text-[var(--cc-text-dark)]"}`} data-testid={testId}>
    <div className={`overline text-[9px] ${highlight ? "text-white/60" : "text-[var(--cc-text-dark-muted)]"}`}>
      {label}
    </div>
    <div className="font-mono-num text-sm font-semibold mt-0.5">{value}</div>
    {sub && (
      <div className={`text-[10px] mt-0.5 ${highlight ? "text-white/60" : "text-[var(--cc-text-dark-muted)]"}`}>
        {sub}
      </div>
    )}
  </div>
);
